import { useSyncExternalStore } from 'react';
import { safeUrl } from '../sw/logic';

/**
 * The installable Stitch app, in the page: the service worker (/sw.js, production builds only),
 * the "new version" prompt, the install prompt, opening a tapped notification, the app badge,
 * and the colour behind the phone's status bar.
 */

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PwaState {
  /** The service worker, once registered. */
  registration: ServiceWorkerRegistration | null;
  /** A new version of Stitch is ready: reload to use it. */
  updateReady: boolean;
  /** The browser can install Stitch now (Chrome, Edge, Android). */
  canInstall: boolean;
  /** Running as the installed app (from the home screen). */
  installed: boolean;
}

let state: PwaState = { registration: null, updateReady: false, canInstall: false, installed: false };
const listeners = new Set<() => void>();
const set = (next: Partial<PwaState>) => {
  state = { ...state, ...next };
  for (const l of listeners) l();
};

export function usePwa(): PwaState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => void listeners.delete(l);
    },
    () => state,
    () => state
  );
}

/** Opened from the home screen (or as an installed desktop app). */
export function isStandalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

/** iPhone and iPad (iPads say they're Macs, but have touch screens). */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || ((navigator as Navigator & { platform?: string }).platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

let deferred: InstallPromptEvent | null = null;
let started = false;
/** The person pressed Reload on the new version prompt. */
let wantsUpdate = false;
let reloading = false;

const reload = () => {
  if (reloading) return;
  reloading = true;
  window.location.reload();
};

/** Called once, from main.tsx. */
export function startPwa(): void {
  if (started) return;
  started = true;
  set({ installed: isStandalone() });

  // Chrome and Edge offer to install: keep the offer for the Install button.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    set({ canInstall: true });
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    set({ canInstall: false, installed: true });
  });

  syncThemeColor();
  new MutationObserver(syncThemeColor).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', syncThemeColor);

  // Opening Stitch counts as seeing what the badge was about.
  const seen = () => {
    if (document.visibilityState === 'visible') void (navigator as Navigator & { clearAppBadge?: () => Promise<void> }).clearAppBadge?.().catch(() => undefined);
  };
  seen();
  document.addEventListener('visibilitychange', seen);

  // Development serves no /sw.js; nor does a page that isn't https (or this machine).
  if (!import.meta.env.PROD || !('serviceWorker' in navigator) || !window.isSecureContext) return;
  const container = navigator.serviceWorker;
  container.addEventListener('message', (e: MessageEvent) => {
    const data = e.data as { type?: string; url?: unknown } | null;
    if (data?.type === 'stitch:open') openInApp(String(data.url ?? '/'));
  });
  container.startMessages();
  let controlled = Boolean(container.controller);
  container.addEventListener('controllerchange', () => {
    // This tab asked for the new version: load it.
    if (wantsUpdate) return reload();
    // Another tab moved to a new version: this one is still running the old one.
    if (controlled) newVersion();
    controlled = true;
  });
  // After the page has loaded, so it doesn't compete with what the page needs first.
  if (document.readyState === 'complete') void register();
  else window.addEventListener('load', () => void register(), { once: true });
}

async function register(): Promise<void> {
  let reg: ServiceWorkerRegistration;
  try {
    reg = await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
  } catch {
    return;
  }
  set({ registration: reg });
  const watch = (w: ServiceWorker | null) => {
    if (!w) return;
    const check = () => {
      // Installed and waiting while an older version runs this page: a new version.
      if (w.state === 'installed' && navigator.serviceWorker.controller) newVersion();
    };
    check();
    w.addEventListener('statechange', check);
  };
  watch(reg.waiting);
  watch(reg.installing);
  reg.addEventListener('updatefound', () => watch(reg.installing));
  // The installed app can stay open for days: look for a new version now and then.
  let checked = Date.now();
  const look = () => {
    checked = Date.now();
    void reg.update().catch(() => undefined);
  };
  window.setInterval(look, 60 * 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - checked > 10 * 60_000) look();
  });
}

/** A new version is ready. A wallboard on a TV has nobody to press Reload, so it takes it by itself. */
function newVersion(): void {
  set({ updateReady: true });
  if (!/^\/tv(\/|$)/.test(window.location.pathname)) return;
  try {
    // At most once in ten minutes, whatever happens.
    const last = Number(sessionStorage.getItem('stitch.tv.updated') ?? 0);
    if (Date.now() - last < 10 * 60_000) return;
    sessionStorage.setItem('stitch.tv.updated', String(Date.now()));
  } catch {
    return;
  }
  applyUpdate();
}

/** Reload into the new version (the new service worker takes over first). */
export function applyUpdate(): void {
  wantsUpdate = true;
  const waiting = state.registration?.waiting;
  if (!waiting) return reload();
  waiting.postMessage({ type: 'stitch:skip-waiting' });
  // If the new version doesn't take over in a moment, reload anyway.
  window.setTimeout(reload, 4000);
}

/** Shows the browser's own install prompt. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const e = deferred;
  if (!e) return 'unavailable';
  deferred = null;
  set({ canInstall: false });
  await e.prompt();
  return (await e.userChoice).outcome;
}

/**
 * Goes to a path in Stitch (a tapped notification). The app's router takes it when a page that
 * knows how is open (it cancels the event); otherwise the page loads afresh there.
 */
export function openInApp(url: string): void {
  const path = safeUrl(url);
  const unhandled = window.dispatchEvent(new CustomEvent('stitch:open', { detail: { url: path }, cancelable: true }));
  if (unhandled) window.location.assign(path);
}

/** The status bar matches the top of the app, in the theme the person chose. */
function syncThemeColor(): void {
  const colour = getComputedStyle(document.documentElement).getPropertyValue('--st-surface').trim();
  if (!colour) return;
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.removeAttribute('media');
    meta.content = colour;
  }
}

// --- the install banner: shown until dismissed, then not for a month ---------------------------

const DISMISSED = 'stitch.install.dismissed';
export function installDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISSED) ?? 0);
    return Date.now() - at < 30 * 86400_000;
  } catch {
    return false;
  }
}
export function dismissInstall(): void {
  try {
    localStorage.setItem(DISMISSED, String(Date.now()));
  } catch {
    /* private mode: just for this visit */
  }
}
